import { useAppSelector } from 'appState/store'
import NotAvailable from 'assets/images/icons/NotAvailable.svg'
import { ReusableModal } from 'components/Modals/ReusableModal'
import { LoadingBox } from '@outbound-ai/ursa'
import { ErrorBox } from '@outbound-ai/ursa'
import { CallStatusEnum, CallTypeToJobTypeEnum, ICall } from 'features/calls/types'
import { getCallSummaryMessage } from 'features/calls/utils/callUtils'
import { useClaimCalls } from 'features/claims/claimsHooks'
import { IClaim } from 'features/claims/types'
import { getClaimCallSummaryPath } from 'features/claims/utils/claimsPathUtils'
import moment from 'moment'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Alert } from 'react-bootstrap'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import { FaAngleLeft, FaAngleRight, FaPhoneVolume } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'
import styled from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { formatJobType } from 'utils/cellFormat'
import { errorHandler } from 'utils/errors/errors'
import { formatPhoneNumber, formatTimeDifference } from 'utils/formatter'

import { ClaimDetailDimishedLabelDiv, ClaimDetailsLogicalGroup, ClaimDetailsLogicalGroupHeader } from './ClaimStyles'
import { Button, OBDropdown } from '@outbound-ai/ursa'

// eslint-disable-next-line import/named
interface IClaimDetailsCallsProps {
  isDiagnosticScreen?: boolean
  onViewTranscript?: Dispatch<SetStateAction<boolean>>
  claim: IClaim
  onChangeCallIndex?: Dispatch<SetStateAction<number>>
}

export const ClaimDetailsCalls = ({
  claim,
  onChangeCallIndex,
  isDiagnosticScreen = false,
  onViewTranscript,
}: IClaimDetailsCallsProps) => {
  const [callIndex, setCallIndex] = useState(0)
  const [callTypeInfoModal, setcallTypeInfoModal] = useState(false)

  const navigate = useNavigate()

  const { isConnected } = useAppSelector((state) => state.calls.talkToPayorRepLiveCall)
  // services
  const { data: unsortedCalls, error, isLoading } = useClaimCalls(claim.oaiClaimId)
  const calls: ICall[] = unsortedCalls?.sort((a, b) => ('' + a.jobRequested).localeCompare(b.jobRequested)) || []

  useEffect(() => {
    if (calls.length) {
      setCallIndex(calls.length - 1)
    }
  }, [calls])

  useEffect(() => {
    onChangeCallIndex && onChangeCallIndex(callIndex)
  }, [callIndex])

  const redirectToClaimCallSummary = (call: ICall) => {
    const tenantId = getTntIdForNavigation()
    navigate(getClaimCallSummaryPath(tenantId, claim!.claimId, call.jbId))
  }

  // handlers
  const handleDecrement = () => calls.length && setCallIndex((state) => (state > 0 ? state - 1 : calls.length - 1))
  const handleIncrement = () => calls.length && setCallIndex((state) => (state < calls.length - 1 ? state + 1 : 0))
  const getInitiatingUserName = (call: ICall) =>
    call?.initiatorUser == null
      ? 'AI Agent'
      : ((call.initiatorUser.firstName ?? '') + ' ' + (call.initiatorUser.lastName ?? '')).trim()

  let callHistoryDiv = (
    <>
      <ClaimDetailsLogicalGroupHeader>
        <CallHistoryHeader>
          <span data-testid="calls-section-header">{isDiagnosticScreen ? 'Test ' : ''}Calls</span>
          {!isDiagnosticScreen && (
            <div className="next-prev-btn-container">
              <button disabled={true}>
                <span className="arrow-icon" data-testid="calls-back-arrow">
                  &lt;
                </span>
              </button>
              <span>0 of 0</span>
              <button disabled={true}>
                <span className="arrow-icon" data-testid="calls-next-arrow">
                  &gt;
                </span>
              </button>
            </div>
          )}
        </CallHistoryHeader>
      </ClaimDetailsLogicalGroupHeader>
      <CallDispositionInnerDiv className="d-flex align-items-center justify-content-center">
        <div className="text-center">
          <img src={NotAvailable} alt="No call records available" />
          <h3 className="no-call-record-text mt-3">
            No{isDiagnosticScreen ? ' Test Calls Recorded' : ' call records available'}
          </h3>
          {!isDiagnosticScreen && (
            <div className="no-call-history-link">
              <a onClick={() => setcallTypeInfoModal(true)}>Learn More</a> <span>about payer calls</span>
            </div>
          )}
        </div>
      </CallDispositionInnerDiv>
    </>
  )

  if (calls && calls[callIndex]) {
    const thisIndex: number = callIndex + 1
    const callDateTime: Date = new Date(
      calls[callIndex].jobStarted ?? calls[callIndex].jobRequested ?? calls[callIndex].jobQueued,
    )
    const call = calls[callIndex]
    callHistoryDiv = (
      <>
        <ClaimDetailsLogicalGroupHeader>
          <CallHistoryHeader>
            <span data-testid="calls-section-header">{isDiagnosticScreen ? 'Past Test ' : ''}Calls</span>
            <div className="next-prev-btn-container">
              <button onClick={handleDecrement}>
                <span className="arrow-icon" data-testid="calls-back-arrow">
                  <FaAngleLeft />
                </span>
              </button>
              <span>
                <OBDropdown
                  className="calls-dropdown"
                  variant="secondary"
                  radius="normal"
                  size="small"
                  buttonName={thisIndex}
                  items={[
                    {
                      items: calls.map((_, index) => ({
                        text: index + 1,
                        onClick: () => setCallIndex(index),
                      })),
                    },
                  ]}
                />
                <span className="totalcall"> of {calls?.length} </span>
              </span>
              <button onClick={handleIncrement}>
                <span className="arrow-icon" data-testid="calls-next-arrow">
                  <FaAngleRight />
                </span>
              </button>
            </div>
          </CallHistoryHeader>
        </ClaimDetailsLogicalGroupHeader>
        <CallDispositionInnerDiv className="claim-details-calls-body">
          {[CallStatusEnum.InProgress, CallStatusEnum.Queued].includes(calls[callIndex].status) ? (
            <>
              <div className="call-inprogress">
                <FaPhoneVolume />

                {CallTypeToJobTypeEnum.claimStatus === calls[callIndex].jobType ? (
                  <>
                    {CallStatusEnum.InProgress === calls[callIndex].status ? (
                      <h3>Call in Progress</h3>
                    ) : (
                      <h3>Call is Queued</h3>
                    )}
                  </>
                ) : (
                  <>{isConnected ? <h3>Call in Progress</h3> : <h3>Call is Queued</h3>}</>
                )}

                <div>
                  <span>Summary will be available shortly after this call is completed.</span>
                </div>
              </div>
            </>
          ) : (
            <>
              {isDiagnosticScreen ? (
                <>
                  <ClaimDetailDimishedLabelDiv data-testid="call-disp-label">Call Date</ClaimDetailDimishedLabelDiv>
                  <div className="calls-inner-description" data-testid="call-disposition">
                    {moment(callDateTime).format('MM/DD/YYYY')}
                  </div>
                  <ClaimDetailDimishedLabelDiv data-testid="call-date-time-label">
                    Call Time
                  </ClaimDetailDimishedLabelDiv>
                  <div className="calls-inner-description" data-testid="call-date-time">
                    {callDateTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div className="call-info">
                      {formatJobType(calls[callIndex].jobType, calls[callIndex].useCase!)}
                    </div>
                    <div className="call-info-holder">
                      <div className="call-info ">
                        <strong className="call-number">{getInitiatingUserName(call)}</strong>{' '}
                        <span className="call-normal">call to</span>{' '}
                        <strong className="call-number">({formatPhoneNumber(calls[callIndex].phoneNumber)})</strong>
                      </div>
                      <div className="call-info timer">
                        {moment(calls[callIndex].jobStarted).local().format('MM/DD/YYYY')} |{' '}
                        {moment(calls[callIndex].jobStarted).local().format('hh:mm')} -{' '}
                        {moment(calls[callIndex].jobCompleted).local().format('hh:mm a')}{' '}
                        {moment(calls[callIndex].jobCompleted).tz(moment.tz.guess()).zoneAbbr()}(
                        {formatTimeDifference(calls[callIndex].jobCompleted!, calls[callIndex].jobStarted!)})
                      </div>
                    </div>
                  </div>
                </>
              )}

              {call?.status === CallStatusEnum.FailedToQueue ||
              call?.status === CallStatusEnum.FailedToComplete ||
              call?.status === CallStatusEnum.Canceled ||
              call?.status === CallStatusEnum.RetryPending ? (
                <Alert variant="danger" className="text-center">
                  {getCallSummaryMessage(calls[callIndex])}
                </Alert>
              ) : (
                <Button
                  variant="secondary"
                  size="normal"
                  radius="normal"
                  width="100%"
                  onClick={() =>
                    isDiagnosticScreen && onViewTranscript ? onViewTranscript(true) : redirectToClaimCallSummary(call)
                  }
                  data-testid="view-summary"
                >
                  View{isDiagnosticScreen ? ' Call Transcript' : ' Summary'}
                </Button>
              )}
            </>
          )}
        </CallDispositionInnerDiv>
      </>
    )
  }
  // TODO: Need to have separate component for Learn more modal
  return (
    <>
      <ClaimDetailsLogicalGroup>
        {isLoading && <LoadingBox message="Loading calls..." />}
        {error && <ErrorBox {...errorHandler.errorToErrorBoxProps(error, ErrorMessagesEnum.CallsError)} />}
        {calls && !error && callHistoryDiv}
      </ClaimDetailsLogicalGroup>
      <ReusableModal show={callTypeInfoModal} setShow={setcallTypeInfoModal} maxWidth="39rem" style={modalStyle}>
        <ModalInnerDiv>
          <h3>
            <FaPhoneVolume /> Payer Call Options
          </h3>
          <Tabs defaultActiveKey="gatherClaimStatusviaIVR" transition={false} className="tab-container">
            <Tab eventKey="gatherClaimStatusviaIVR" title="Gather Claim Status via IVR" className="tab-button">
              <p>
                This initiates an automated call to the payer in the background, freeing you to do other things while
                Outbound’s AI agent navigates the payer’s IVR menus and attempts to capture the claim status.{' '}
              </p>
              <p>
                Once the call is complete, you can review its details, notes, transcript and any extracted information
                in the call summary. Extracted information is also viewable here, on the claim details page, with all
                added and updated fields highlighted in blue. You can access version history here by clicking on the
                blue chips and from there, edit the value or revert to previous versions as needed.
              </p>
              <p className="small">
                1 Payer Billed Amount is a protected value, used to help identify the claim, so Outbound does not
                automatically update this field in the claim details, based on extracted versions from payer calls. To
                edit this value , access Billed Amount version history by clicking it, here on the claim details page.
              </p>
            </Tab>
            <Tab eventKey="talktoPayorRep" title="Talk to Payer Rep">
              <p>
                This initiates a call to the payer in a minimized window, freeing you to do other things while
                Outbound’s AI agent navigates the payer’s IVR menus and waits on hold on your behalf.
              </p>
              <p>
                You can open and observe the live call or takeover at any time, removing the AII agent from the
                conversation. You can also minimize the call view again, up until the payer agent joins the call.
              </p>
              <p>
                Once the call is complete, you can review its details, notes, transcript and extracted information in
                the call summary. Extracted information is also viewable here on the claim details page, with added
                values highlighted in blue and user-edited values highlighted in green. You can access version history
                per value by clicking on the blue and green chips and from there, edit the value or revert to previous
                versions as needed.
              </p>
              <p className="small">
                1 Payer Billed Amount is a protected value, used to help identify the claim, so Outbound does not
                automatically update this field in the claim details, based on extracted versions from payer calls. To
                edit this value , access Billed Amount version history by clicking it, here on the claim details page.
              </p>
            </Tab>
          </Tabs>
          <button onClick={() => setcallTypeInfoModal(false)} className="learn-more-done-btn">
            Done
          </button>
        </ModalInnerDiv>
      </ReusableModal>
    </>
  )
}

const CallHistoryHeader = styled.div`
  display: flex;
  align-items: center;
  height: 80%;
  .next-prev-btn-container {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    font-style: normal;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    ${({ theme }) => theme.typography.m.normal};
    line-height: 135%;
    color: #576573;
  }
  .next-prev-btn-container button {
    background: none;
    align-items: center;
    align-self: center;
  }

  .dropdown-menu {
    max-height: 200px;
    border-radius: 0px;
    overflow: auto;
  }

  .dropdown,
  .calls-dropdown {
    float: left;
    margin-right: 5px;
    button {
      background: #fff;
      border: 1px solid #d9d9d9;
      border-radius: 4px;
      color: #999;
      cursor: pointer;
      ${({ theme }) => theme.typography.s.medium};
      text-decoration: none;
    }
    .btn:focus {
      outline: none;
      box-shadow: none;
    }
    button::click {
      outline: none;
      box-shadow: none;
    }
    button::focus {
      outline: none;
      box-shadow: none;
    }
    button::focus-outline {
      outline: none;
      box-shadow: none;
    }
    button::hover {
      outline: none;
      box-shadow: none;
    }
  }
  .calls-dropdown #dropdown-basic {
    background: #fff;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    color: #999;
    text-decoration: none;
  }
  .totalcall {
    position: relative;
    top: 5px;
  }
  .arrow-icon {
    width: 1rem;
    height: 1rem;
    border-radius: 50px;
    margin: 0 10px;
    opacity: 0.3;
    color: #32325d;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    ${({ theme }) => theme.typography.xs.normal};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
    line-height: 14;
  }
`
const CallDispositionInnerDiv = styled.div`
  font-family: 'Milliard';
  font-style: normal;
  height: 11.5rem;
  padding: 1rem 1rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  .view-summary-btn {
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.colors.grey.lightest};
    border: 1px solid ${({ theme }) => theme.colors.info.darker};
    border-radius: 5px;
    color: ${({ theme }) => theme.colors.primary.neutral};
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    ${({ theme }) => theme.typography.s.normal};
    line-height: ${({ theme }) => theme.spacing.s};
  }

  .claim-details-calls-body {
    display: flex;
    svg {
      height: 37px;
      width: 27px;
    }
  }

  .alert {
    padding: 0.3rem 1rem;
  }

  .call-info-holder {
    padding-top: ${({ theme }) => theme.spacing.xxs};
  }

  .call-number {
    margin-top: ${({ theme }) => theme.spacing.xxs};
    margin-bottom: ${({ theme }) => theme.spacing.xxxs};
    ${({ theme }) => theme.typography.m.medium};
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }

  .call-normal {
    ${({ theme }) => theme.typography.m.normal};
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }

  .timer {
    ${({ theme }) => theme.typography.s.normal};
    color: ${({ theme }) => theme.colors.neutral.neutral};
    margin-bottom: ${({ theme }) => theme.spacing.m};
  }
  .calls-inner-description {
    margin-bottom: 0.75rem;
    font-style: normal;
    ${({ theme }) => theme.typography.s.medium};
    color: ${({ theme }) => theme.colors.grey.dark};
  }
  .no-call-record-text {
    font-weight: 600;
    font-size: 1.125rem;
    line-height: 1.3125rem;
    color: ${({ theme }) => theme.colors.primary.darker};
  }
  .no-call-history-link a {
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 150%;
    text-decoration-line: underline;
    color: ${({ theme }) => theme.colors.info.darker};
    cursor: pointer;
  }
  .no-call-history-link span {
    margin-left: 5px;
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 150%;
    color: ${({ theme }) => theme.colors.primary.darker};
  }
  .call-inprogress {
    text-align: center;

    svg {
      color: ${({ theme }) => theme.colors.secondary.dark};
      width: 27px;
      height: 37px;
    }
    h3 {
      font-family: ${({ theme }) => theme.fontFamily.milliard};
      font-weight: bold;
      color: ${({ theme }) => theme.colors.primary.darker};
      margin: 0.5rem 0rem;
    }
    span {
      font-weight: ${({ theme }) => theme.fontWeight.normal};
      ${({ theme }) => theme.typography.s.normal};
      line-height: 128%;
      text-align: center;
      color: ${({ theme }) => theme.colors.grey.darker};
    }
  }
`
const modalStyle = {
  headerheight: '42px',
  shoeCloseButton: false,
}

const ModalInnerDiv = styled.div`
  margin: 2rem 2.875rem;

  h3 {
    font-family: ${({ theme }) => theme.fontFamily.milliard};
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 140%;
    color: ${({ theme }) => theme.colors.grey.darker};
    text-align: center;
  }
  h3 svg {
    margin-right: 0.3rem;
    color: ${({ theme }) => theme.colors.secondary.dark};
    ${({ theme }) => theme.typography.h2.normal};
  }

  p {
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    margin-top: 20px;
    line-height: 150%;
    color: ${({ theme }) => theme.colors.grey.darker};
  }
  .tab-container {
    background: rgba(233, 243, 250, 0.5);
  }
  .nav-tabs {
    display: table;
    width: 100%;
    table-layout: fixed;
    border: 0px;
    background: rgba(233, 243, 250, 0.5);
    border-radius: 5px;
    margin-top: 25px;
    padding: 3px 5px;
  }
  .nav-tabs .nav-link {
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 600;
    font-size: 15px;
    line-height: 150%;
    text-align: center;
    color: #2685ca;
    float: left;
    margin: 0px;
    padding: 5px 10px;
    display: table-cell;
    width: 50%;
  }
  .nav-tabs .nav-link.active {
    background: #ffffff;
    border: 1px solid #2685ca;
    border-radius: 5px;
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 600;
    grid-auto-flow: column;
    font-size: 15px;
    margin: 0px;
    padding: 5px 10px;
    line-height: 150%;
    text-align: center;
    color: #2685ca;
  }
  .nav-tabs .nav-link:hover {
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 600;
    font-size: 15px;
    line-height: 150%;
    text-align: center;
    color: #2685ca;
    float: left;
    padding: 5px 10px;
    display: table-cell;
    width: 50%;
  }
  .small {
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 400;
    font-size: 9px;
    margin-top: 20px;
    line-height: 150%;
    color: ${({ theme }) => theme.colors.grey.light};
  }
  .learn-more-done-btn {
    width: 100%;
    height: 40px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 14px 28px;
    margin-top: 30px;
    opacity: 0.8;
    border-radius: 6px;
    background: #2685ca;
    font-family: 'Milliard';
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 150%;
    color: #fff;
  }
`
